import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Shield, User } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const EMPTY = { name: "", email: "", password: "", role: "employee", phone: "", department: "" };

export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created"); setShowForm(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#DC2A1C] font-semibold mb-1">Admin</div>
          <h1 className="text-3xl font-black tracking-tight">User Management</h1>
        </div>
        <button onClick={() => { setForm(EMPTY); setShowForm(true); }} className="tl-btn flex items-center gap-2" data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add User</button>
      </div>

      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA] text-xs uppercase tracking-[0.1em] text-gray-600">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Department</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Created</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-testid="users-table">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-[#F8F9FA]">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-semibold ${u.role === "admin" ? "bg-[#DC2A1C]/10 text-[#DC2A1C]" : "bg-gray-100 text-gray-600"}`}>
                    {u.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}{u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{u.department || "—"}</td>
                <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  {me.id !== u.id && <button onClick={() => remove(u)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <form className="bg-white rounded-xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="user-form">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">New User</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button>
            </div>
            <F label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="user-name" />
            <F label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="user-email" />
            <F label="Phone (optional)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="user-phone" />
            <F label="Department (optional)" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="user-department" />
            <F label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="user-password" />
            <div>
              <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]">
                <option value="employee">Employee</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button type="submit" className="tl-btn w-full" data-testid="user-form-submit">Create User</button>
          </form>
        </div>
      )}
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.12em] font-semibold text-gray-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} className="w-full bg-[#F8F9FA] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#DC2A1C]" />
    </div>
  );
}
